import {
  Button,
  Flex,
  FormItem,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  TextInput,
  Textarea,
  Typography,
} from "@aircall/tractor";
import { useForm } from "@tanstack/react-form";
import styled from "@xstyled/styled-components";
import { TFunction, useTranslation } from "react-i18next";

import { ScorecardLanguageSelect } from "../ScorecardLanguageSelect";

import { ScorecardPreCreateFormValue, ScorecardPreCreateModalProps } from ".";

const StyledModalBody = styled(ModalBody)`
  box-shadow: none;
`;

export const validateTitle = (value: string, t: TFunction) => {
  if (!value.trim().length) {
    return t("score_card.required_message");
  }
  return undefined;
};

// Render a modal to fill the name, description and select the language of the scorecard,
// When submitted, we redirect to the new scorecard page with the data filled
export function ScorecardPreCreateModal({
  show,
  onHide,
  onSubmit,
}: ScorecardPreCreateModalProps) {
  const { t } = useTranslation();
  const form = useForm<ScorecardPreCreateFormValue>({
    defaultValues: {
      name: "",
      description: "",
      language: "en",
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <ModalDialog
      show={show}
      onHide={onHide}
      size="regular"
      data-test="pre-create-scorecard-modal"
    >
      <ModalHeader closeButton={false}>
        <Typography variant="headingBoldXS" color="text-base" py="s">
          {t("score_card.new_scorecard")}
        </Typography>
      </ModalHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <StyledModalBody py="m" px="s">
          <Flex gap="m" flexDirection="column">
            <form.Field
              name="name"
              validators={{ onChange: ({ value }) => validateTitle(value, t) }}
            >
              {(nameField) => (
                <FormItem
                  label={t("score_card.name")}
                  validationStatus={
                    nameField.state.meta.errorMap.onChange ? "error" : undefined
                  }
                  helpText={nameField.state.meta.errorMap.onChange || ""}
                >
                  <TextInput
                    value={nameField.state.value}
                    onChange={(e) => nameField.handleChange(e.target.value)}
                    data-test="pre-create-scorecard-name-input"
                  />
                </FormItem>
              )}
            </form.Field>
            <form.Field name="description">
              {(descriptionField) => (
                <FormItem
                  label={t("score_card.description")}
                  helpText={t("optional")}
                >
                  <Textarea
                    value={descriptionField.state.value}
                    onChange={(e) => {
                      descriptionField.handleChange(e.target.value);
                    }}
                    data-test="pre-create-scorecard-description-input"
                  />
                </FormItem>
              )}
            </form.Field>
            <form.Field name="language">
              {(languageField) => (
                <FormItem label={t("score_card.language")}>
                  <ScorecardLanguageSelect
                    onChange={(v) => {
                      languageField.handleChange(v);
                    }}
                    value={languageField.state.value}
                    data-test="pre-create-scorecard-select-language"
                  />
                </FormItem>
              )}
            </form.Field>
          </Flex>
        </StyledModalBody>
        <ModalFooter>
          <Flex justifyContent="end" gap="s">
            <Button
              mode="ghost"
              variant="secondary"
              type="button"
              size="regular"
              onClick={onHide}
              data-test="pre-create-scorecard-cancel-button"
            >
              {t("cancel")}
            </Button>
            <form.Subscribe selector={(state) => [state.canSubmit]}>
              {([canSubmit]) => (
                <Button
                  variant="primary"
                  type="submit"
                  size="regular"
                  disabled={!canSubmit}
                  data-test="pre-create-scorecard-create-button"
                >
                  {t("create")}
                </Button>
              )}
            </form.Subscribe>
          </Flex>
        </ModalFooter>
      </form>
    </ModalDialog>
  );
}
